import {
    API_ENDPOINTS,
    customToast,
    DataColumnInterface,
    DataTable,
    DateFormats,
    formatDateFns,
    FormInput,
    FormInputType,
    Modal,
    PageLayout,
    Permissions,
    replaceKeyWithValue,
    Roles,
    route,
    SelectOption,
    StringHelpers,
    useAuthorisationContext,
    useDisabledContext,
    useLangContext,
    useQuery,
    useUserContext,
    Variants
} from "carrier-fe";
import axios from "axios";
import {
    approveDocument,
    deleteDocument,
    downloadAll,
    rejectDocument
} from "./document-helpers";
import { useMemo, useState } from "react";
import { DocumentResourceType, DocumentModalType } from "../../types/documents";

function DocumentsList() {
    const { fields, crud } = useLangContext();
    const { disabled, setDisabled } = useDisabledContext();
    const [activeModal, setActiveModal] = useState<DocumentModalType>(null);
    const [companyDocument, setCompanyDocument] = useState<Partial<DocumentResourceType>>();
    const { user } = useUserContext();
    const { hasRoleAccess, hasPermissionAccess } = useAuthorisationContext();

    const multipleDocumentFiles = 
        companyDocument?.files && companyDocument?.files?.length > 1;

    const { data: documentTypes } = useQuery<{ data: SelectOption[] }>(
        route(API_ENDPOINTS.GENERIC.SELECT.DOCUMENT_TYPES.COMPANY),
        {
            method: 'GET'
        }
    );

    const openModal = (modalType: DocumentModalType) => setActiveModal(modalType);
    const closeModal = () => {
        setActiveModal(null);
        setCompanyDocument({});
    };

    const downloadSyntheticFile = async (data: DocumentResourceType) => {
        const file = data.files?.[0];
        if (!file?.download_url) {
            customToast({
                title: 'No file available to download',
                variant: Variants.Danger,
            });
            return;
        }

        setDisabled(true);
        try {
            const response = await axios.get(file.download_url, {
                responseType: 'blob',
            });
            const objectUrl = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = objectUrl;
            link.setAttribute('download', `${file.name || 'document'}.pdf`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(objectUrl);
        } catch (error: any) {
            customToast({
                title:
                    error?.response?.data?.message
                    || 'An error occurred. Please try again.',
                variant: Variants.Danger,
            });
        } finally {
            setDisabled(false);
        }
    };

    const handleOptionSelect = (action: unknown, data: DocumentResourceType) => {
        const { id, company_name, document_type, files } = data;

        const actions: Record<string, () => void> = {
            approve: () => {
                openModal('approve');
                setCompanyDocument({
                    id,
                    company_name,
                    document_type,
                });
            },
            reject: () => {
                openModal('reject');
                setCompanyDocument({
                    id,
                    company_name,
                    document_type,
                });
            },
            delete: () => {
                openModal('delete');
                setCompanyDocument({
                    id,
                    company_name,
                    document_type,
                });
            },
            download: () => {
                // Synthetic rows (e.g. per-distributor warranty certificates)
                // point at an authenticated API endpoint; proxy via axios so
                // the bearer token is attached.
                if (data.synthetic) {
                    downloadSyntheticFile(data);
                    return;
                }

                openModal('download');
                setCompanyDocument({
                    id,
                    company_name,
                    document_type,
                    files
                });
            },
        };

        if (typeof action === 'string' && actions[action]) {
            actions[action]();
        }
    };

    const title = useMemo(
        () => StringHelpers.title(
            replaceKeyWithValue(
                crud?.pages?.index?.title || ':model List',
                'model',
                crud?.sub_titles?.documents
                    || 'Documents'
            )
        ),
        [crud]
    );

    const colMapping = (data: DocumentResourceType): DataColumnInterface[] => {
        const alreadyApprovedOrRejected = 
            !!data?.approved_at || !!data.rejected_at;
            
        return [
            {
                id: 'document_type',
                label: `${
                    crud?.models?.document 
                    || 'Document'
                } ${crud?.table_columns?.type
                    || 'Type'
                }`, 
                value: data?.document_type || '-', 
                sortable: true,
            },
            {
                id: 'company_name',
                label: StringHelpers.title(
                    fields?.company_name
                    || 'company name'
                ),
                value: data?.company_name || '-',
                sortable: true,
                visibleCondition: () => 
                    hasRoleAccess(Roles.PLATFORM_MANAGER) || 
                    hasRoleAccess(Roles.SUPER_ADMIN)
            },
            {
                id: 'created_at',
                label: StringHelpers.title(
                    fields?.created_at
                    || 'Created'
                ), 
                value: formatDateFns(
                    data?.created_at 
                    || '-', 
                    DateFormats.DATE
                ),
                sortable: true,
            },
            {
                id: 'approval_status',
                label: StringHelpers.title(
                    fields?.approval_status
                    || 'approval status'
                ), 
                value: data?.approval_status,
            },
            {
                id: 'approved_at',
                label: StringHelpers.title(
                    fields?.approval_date
                    || 'approval date'
                ),
                value: formatDateFns(
                    data?.approved_at
                    || '-',
                    DateFormats.DATE
                ),
                sortable: true,
            },
            {
                id: 'approved_by',
                label: StringHelpers.title(
                    fields?.approved_by
                    || 'approved by'
                ),
                value: data?.approved_by
                    || '-',
                sortable: true,
            },
            {
                id: 'expiry_date',
                label: fields?.expiry_date
                    || 'Expiry Date',
                value: formatDateFns(
                    data?.expiry_date
                    || '-',
                    DateFormats.DATE
                ),
                sortable: true,
            },
            {
                id: 'actions',
                label: StringHelpers.title(
                    fields?.actions 
                    || 'Actions'
                ),
                value: <FormInput
                    type="select"
                    label={StringHelpers.title(
                        fields?.actions 
                        || 'Action'
                    )}
                    placeholder={StringHelpers.title(
                        fields?.select 
                        || 'Select'
                    )}
                    name="actions"
                    value=""
                    onChange={(value) => {
                        if (!!value && typeof value === 'string') {
                            handleOptionSelect(value, data);
                        }
                    }}
                    options={[
                        ...(!data?.synthetic && !alreadyApprovedOrRejected
                            && hasPermissionAccess(Permissions.DOCUMENT_APPROVE)) ? [{
                                label: crud?.select_labels?.approve
                                    || 'Approve',
                                value: 'approve',
                            }] : [],
                        ...(!data?.synthetic && !alreadyApprovedOrRejected
                            && hasPermissionAccess(Permissions.DOCUMENT_REJECT)) ? [{
                                label: crud?.select_labels?.reject
                                    || 'Reject',
                                value: 'reject',
                            }] : [],
                        ...(!data?.synthetic
                            && hasPermissionAccess(Permissions.DOCUMENT_DELETE)) ? [{
                            label: crud?.select_labels?.delete
                                || 'Delete',
                            value: 'delete',
                        }] : [],
                        ...hasPermissionAccess(Permissions.DOCUMENT_SHOW) ? [{
                            label: crud?.select_labels?.download
                                || 'Download',
                            value: 'download',
                        }] : [],
					]}
                />
            },
        ];
    };

    const filters: FormInputType[] = [
        {
            name: 'approval_status',
            label: StringHelpers.title(
                fields?.approval_status
                || 'Approval Status'
            ),
            type: 'searchable-select',
            value: '',
            placeholder: StringHelpers.title(
                fields?.please_select 
                || 'please select'
            ),
            options: [
                {
                    label: StringHelpers.title(
                        fields?.approved
                        || 'approved'
                    ),
                    value: fields?.approved
                        || 'approved',
                },
                {
                    label: StringHelpers.title(
                        fields?.rejected
                        || 'rejected'
                    ),
                    value: fields?.rejected
                        || 'rejected',
                },
                {
                    label: StringHelpers.title(
                        fields?.pending
                        || 'pending'
                    ),
                    value: fields?.pending
                        || 'pending',
                },
            ],
        },
        {
            name: 'document_type',
            label: `${
                crud?.models?.document 
                || 'Document'
            } ${crud?.table_columns?.type
                || 'Type'
            }`,
            type: 'searchable-select',
            value: '',
            placeholder: StringHelpers.title(
                fields?.please_select 
                || 'please select'
            ),
            options: documentTypes?.data,
        },
    ];

    const modalDescription = () => {
        return (
            <ul>
                <li>
                    <strong>
                        {StringHelpers.title(
                            fields?.company_name
                            || 'company name'
                        )}
                    </strong>: {companyDocument?.company_name}
                </li>
                <li>
                    <strong>
                        {`${
                            crud?.models?.document 
                            || 'Document'
                        } ${crud?.table_columns?.type
                            || 'Type'
                        }`}
                    </strong>: {companyDocument?.document_type}
                </li>
            </ul>
        )
    };

    return (
        <PageLayout title={title || 'Documents List'}>
            <DataTable<DocumentResourceType>
                routeEndpoint={route(hasRoleAccess(Roles.COMPANY_MANAGER) && user?.individual?.company_id
                    ? API_ENDPOINTS.GENERIC.DOCUMENT.INDEX + `?filter[company_id]=${user?.individual?.company_id}&`
                    : API_ENDPOINTS.GENERIC.DOCUMENT.INDEX
                )}
                colMapping={colMapping}
                variant={Variants.Info}
                incrementPaginationButtons
                filters={filters}
                refreshOnChange={companyDocument?.id}
                helperTextItems={[
                    fields?.approved_by
                        || 'approved by',
                    ...hasRoleAccess(Roles.PLATFORM_MANAGER) ?
                    [fields?.company_name
                        || 'company name'
                    ] : [],
                ]}
            />

            <Modal
                open={activeModal === 'approve'}
                onClose={closeModal}
                title={StringHelpers.title(
                    replaceKeyWithValue(
                        crud?.modals?.approve?.title
                            || 'Approve :name',
                        'name',
                        crud?.models?.document
                            || 'Document'
                    )
                )}
                confirmText={disabled
                    ? crud?.buttons?.approve?.submitting
                        || 'Approving...'
                    : crud?.buttons?.approve?.default
                        || 'Approve'
                }
                closeText={
                    crud?.buttons?.cancel?.default 
                        || 'Cancel'
                }
                variant={Variants.Primary}
                onConfirm={() => approveDocument(
                    setDisabled,
                    closeModal,
                    companyDocument?.id || ''
                )}
            >
                <p>
                    {replaceKeyWithValue(
                        crud?.modals?.approve?.alt_description
                            || 'Are you sure you want to approve the following :name?',
                        'name',
                        StringHelpers.lower(
                            crud?.models?.document
                            || 'Document'
                        )
                    )}
                </p>
                {modalDescription()}
            </Modal>
            
            <Modal
                open={activeModal === 'reject'}
                onClose={closeModal}
                title={StringHelpers.title(
                    replaceKeyWithValue(
                        crud?.modals?.reject?.title
                            || 'Reject :name',
                        'name',
                        crud?.models?.document
                            || 'Document'
                    )
                )}
                confirmText={disabled
                    ? crud?.buttons?.reject?.submitting
                        || 'Rejecting...'
                    : crud?.buttons?.reject?.default
                        || 'Reject'
                }
                closeText={
                    crud?.buttons?.cancel?.default 
                        || 'Cancel'
                }
                variant={Variants.Primary}
                onConfirm={() => rejectDocument(
                    setDisabled,
                    closeModal,
                    companyDocument?.id || ''
                )}
            >
                <p>
                    {replaceKeyWithValue(
                        crud?.modals?.reject?.alt_description
                            || 'Are you sure you want to reject the following :name?',
                        'name',
                        StringHelpers.lower(
                            crud?.models?.document
                            || 'Document'
                        )
                    )}
                </p>
                {modalDescription()}
            </Modal>

            <Modal
                open={activeModal === 'delete'}
                onClose={closeModal}
                title={StringHelpers.title(
                    replaceKeyWithValue(
                        crud?.modals?.delete?.title
                            || 'Delete :name',
                        'name',
                        crud?.models?.document
                            || 'Document'
                    )
                )}
                confirmText={disabled
                    ? crud?.buttons?.delete?.submitting
                        || 'Deleting...'
                    : crud?.buttons?.delete?.default
                        || 'Delete'
                }
                closeText={
                    crud?.buttons?.cancel?.default 
                        || 'Cancel'
                }
                variant={Variants.Primary}
                onConfirm={() => deleteDocument(
                    setDisabled,
                    closeModal,
                    companyDocument?.id || ''
                )}
            >
                {modalDescription()}
            </Modal>

            <Modal
                open={activeModal === 'download'}
                onClose={closeModal}
                title={replaceKeyWithValue(
                    crud?.modals?.download?.title
                        || 'Download :name',
                    'name',
                    fields?.files
                        || 'files'
                )}
                closeText={
                    crud?.buttons?.cancel?.default 
                        || 'Cancel'
                }
                variant={Variants.Primary}
                {...(multipleDocumentFiles && {
                    onConfirm: () => downloadAll(
                        setDisabled,
                        closeModal,
                        companyDocument || {}
                    ),
                    confirmText: disabled
                        ? crud?.buttons?.download?.submitting || 'Downloading...'
                        : replaceKeyWithValue(
                            crud?.buttons?.download?.default 
                                || 'Download :any', 
                            'any',
                            fields?.all
                                || 'All'
                        )
                })}
            >
                <p>
                    {replaceKeyWithValue(
                        crud?.modals?.download?.description
                            || 'To download a :name, click on the link(s)',
                        'name',
                        fields?.file 
                            || 'file'
                    )}
                </p>
                <ul>
                    {companyDocument?.files?.map((file) => 
                        <li key={file.name}>
                            <a 
                                href={file.download_url}
                                target='_blank'
                                rel='noopener noreferrer'
                            >
                                {file.name}
                            </a>
                        </li>
                    )}    
                </ul>
            </Modal>
        </PageLayout>
    );
};

export default DocumentsList;